/**
 * Login Modal Component
 *
 * Displays a modal for user login with email/password.
 * Includes link to signup and password reset.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import MaterialIcon from '../MaterialIcon';
import './AuthModals.css';

const LoginModal = ({ isOpen, onClose, onSwitchToSignup, onSwitchToPasswordReset }) => {
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Success! Close modal
        onClose();
        setEmail('');
        setPassword('');
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError('');
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="auth-modal-header">
          <h2>Welcome Back</h2>
          <button className="auth-modal-close" onClick={handleClose} aria-label="Close modal">
            <MaterialIcon name="close" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-modal-form">
          {error && (
            <div className="auth-error-message">
              <MaterialIcon name="error" />
              <span>{error}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="auth-form-group">
            <label htmlFor="login-email">Email</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="email" />
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="auth-form-group">
            <label htmlFor="login-password">Password</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="lock" />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                className="auth-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <MaterialIcon name={showPassword ? 'visibility_off' : 'visibility'} />
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="auth-forgot-password">
            <button
              type="button"
              className="auth-link-button"
              onClick={onSwitchToPasswordReset}
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-submit-button"
            disabled={loading || !email || !password}
          >
            {loading ? (
              <>
                <span className="auth-spinner" />
                Logging in...
              </>
            ) : (
              <>
                <MaterialIcon name="login" />
                Log In
              </>
            )}
          </button>
        </form>

        {/* Footer - Switch to Signup */}
        <div className="auth-modal-footer">
          <p>
            Don't have an account?{' '}
            <button className="auth-link-button" onClick={onSwitchToSignup} disabled={loading}>
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

LoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSwitchToSignup: PropTypes.func.isRequired,
  onSwitchToPasswordReset: PropTypes.func.isRequired,
};

export default LoginModal;
